/**
 * Spec Phase 7 Slice 7E — smart numeral chip.
 * Phase 8 Slice 8C — flag-promoted tooltip when an examiner flag
 *                    matches an undefined chip.
 *
 * Replaces the Phase 4 read-only span with an interactive
 * component that:
 *   - emits ``numeral:hover`` / ``numeral:hover-out`` /
 *     ``numeral:click`` events on the shared bus
 *     (Slice 7F docks listen)
 *   - shows a tooltip with "{numeral} — {label}" plus a
 *     "Jump to definition" hint
 *   - renders the warn state (amber outline) when the numeral
 *     isn't in the matter's reference-signs registry
 *   - when an open ``numeral_undefined`` examiner flag exists
 *     for the same numeral (post-Phase-8 review), the tooltip
 *     promotes from "Numeral N not defined" to
 *     "Examiner flag: numeral N · click to open Examiner dock"
 *
 * Click semantics: a click publishes ``numeral:click`` and
 * lets the bus listeners drive the cross-rail jump (the
 * Reference Signs dock auto-scrolls to the row; the Frozen
 * Drawings dock pulses any figure that places this numeral).
 */
import React from 'react';
import {
  publish,
  type NumeralEvent,
} from '@/lib/numeralEventBus';
import { useSpecStore } from '@/store/specStore';

interface Props {
  numeral: string;
  label?: string | null;
  /** True when the numeral is not present in the matter's
   * reference-signs registry. Renders amber outline + warn
   * tooltip wording. */
  isUndefined?: boolean;
  /** Source surface, forwarded to event listeners. */
  source?: NumeralEvent['source'];
}

export default function NumeralChip({
  numeral,
  label,
  isUndefined = false,
  source = 'unknown',
}: Props): JSX.Element {
  // Phase 8 Slice 8C — promote tooltip when the examiner has
  // already flagged this undefined numeral.
  const flagPromoted = useSpecStore((s) =>
    s.examinerFlags.some(
      (f) =>
        f.flag_kind === 'numeral_undefined'
        && f.term === numeral
        && (f.status === 'open' || f.status === 'acknowledged'),
    ),
  );

  const baseTitle = label ? `${numeral} — ${label}` : numeral;
  let tooltip: string;
  if (flagPromoted) {
    tooltip =
      `Examiner flag: numeral ${numeral} not defined.\n`
      + `Click to open Examiner dock.`;
  } else if (isUndefined) {
    tooltip = `${numeral} — not in reference signs registry. Click to flag.`;
  } else {
    tooltip = `${baseTitle}\nClick to jump to definition`;
  }

  const cls = (isUndefined || flagPromoted) ? 's6-num s6-num-warn' : 's6-num';

  const onMouseEnter = (): void => {
    publish({
      kind: 'numeral:hover',
      numeral,
      undefined_: isUndefined,
      source,
    });
  };
  const onMouseLeave = (): void => {
    publish({
      kind: 'numeral:hover-out',
      numeral,
      undefined_: isUndefined,
      source,
    });
  };
  const onClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
    e.stopPropagation();
    publish({
      kind: 'numeral:click',
      numeral,
      undefined_: isUndefined,
      source,
    });
  };

  return (
    <span
      className={cls}
      data-num={numeral}
      data-undefined={isUndefined ? 'true' : undefined}
      data-testid={`spec-numeral-chip-${numeral}`}
      title={tooltip}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          publish({
            kind: 'numeral:click',
            numeral,
            undefined_: isUndefined,
            source,
          });
        }
      }}
    >
      {numeral}
    </span>
  );
}
